import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const BANNER_KEY = 'sayitnow_return_banner_shown';
const GIFT_PROMPT_KEY = 'sayitnow_post_share_gift_prompt';

const copy = {
  en: { message: 'You showed up today.', giftMessage: 'Message shared. Want to pair it with a meaningful gift?', giftCta: 'Browse Gift Store' },
  es: { message: 'Hoy te presentaste.', giftMessage: 'Mensaje compartido. ¿Quieres acompañarlo con un regalo significativo?', giftCta: 'Ver Tienda de Regalos' },
  fr: { message: 'Vous vous êtes montré aujourd\'hui.', giftMessage: 'Message partagé. Voulez-vous l\'accompagner d\'un cadeau significatif ?', giftCta: 'Voir la Boutique Cadeaux' },
  pt: { message: 'Você apareceu hoje.', giftMessage: 'Mensagem compartilhada. Quer combinar com um presente significativo?', giftCta: 'Ver Loja de Presentes' },
  ar: { message: 'لقد حضرت اليوم.', giftMessage: 'تمت مشاركة الرسالة. هل تريد إرفاقها بهدية ذات معنى؟', giftCta: 'تصفح متجر الهدايا' },
  ja: { message: '今日、あなたは現れました。', giftMessage: 'メッセージを共有しました。心のこもったギフトも添えますか？', giftCta: 'ギフトストアを見る' },
  ko: { message: '오늘 나타났습니다.', giftMessage: '메시지가 공유되었습니다. 의미 있는 선물도 함께 보시겠어요?', giftCta: '기프트 스토어 보기' },
  id: { message: 'Anda hadir hari ini.', giftMessage: 'Pesan sudah dibagikan. Ingin dipasangkan dengan hadiah bermakna?', giftCta: 'Lihat Toko Hadiah' },

  hi: { message: 'आप आज आए।', giftMessage: 'संदेश शेयर किया गया। क्या आप इसे एक सार्थक उपहार के साथ जोड़ना चाहते हैं?', giftCta: 'गिफ्ट स्टोर ब्राउज़ करें' },
};

export function DashboardReturnBanner() {
  const [visible, setVisible] = useState(false);
  const [showGiftPrompt, setShowGiftPrompt] = useState(false);
  const { language } = useLanguage();
  const t = lc(copy, language);

  useEffect(() => {
    const shouldShowGiftPrompt = sessionStorage.getItem(GIFT_PROMPT_KEY) === '1';

    if (shouldShowGiftPrompt) {
      sessionStorage.removeItem(GIFT_PROMPT_KEY);
      setShowGiftPrompt(true);
      setVisible(true);

      const timer = setTimeout(() => setVisible(false), 10000);
      return () => clearTimeout(timer);
    }

    const publishEvent = sessionStorage.getItem('sayitnow_post_publish_event');
    if (!publishEvent) return;

    try {
      const event = JSON.parse(publishEvent);
      if (event.dismissed) {
        const shownFor = sessionStorage.getItem(BANNER_KEY);
        if (shownFor === event.id) return;

        sessionStorage.setItem(BANNER_KEY, event.id);
        setVisible(true);

        const timer = setTimeout(() => setVisible(false), 10000);
        return () => clearTimeout(timer);
      }
    } catch {
      // ignore
    }
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.5 }}
          className="mb-6 text-center py-3 px-4 rounded-2xl bg-primary/5 border border-primary/10"
        >
          <p className="text-sm font-medium text-primary">
            💚 {showGiftPrompt ? t.giftMessage : t.message}
          </p>
          {showGiftPrompt && (
            <div className="mt-2">
              <Link
                to="/gift-store"
                className="text-sm font-medium text-primary hover:underline"
              >
                {t.giftCta}
              </Link>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
